import { Link } from "react-router-dom";
import { ArrowRight, Heart, Target, Eye, Globe, Users, Shield, BookOpen, Award } from "lucide-react";
import { Button } from "@/components/ui/button";

const HERO_IMAGE = "https://images.unsplash.com/photo-1630068846062-3ffe78aa5049?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHx0ZWFtJTIwY29sbGFib3JhdGlvbiUyMGRpdmVyc2UlMjBncm91cCUyMG1lZXRpbmclMjBub25wcm9maXR8ZW58MHx8fHwxNzcwMzI1MTgxfDA&ixlib=rb-4.1.0&q=85";

const TRACEY_DENT_IMAGE = "https://customer-assets.emergentagent.com/job_cd80ce05-d147-45ff-ad2d-22ce382058f2/artifacts/m03xi96c_7977.jpg";

const values = [
  {
    icon: Globe,
    title: "Community First",
    description: "We believe lasting change starts at the grassroots level, within our communities."
  },
  {
    icon: Shield,
    title: "Non-Violence",
    description: "We are committed to peaceful resolution of conflicts at all levels."
  },
  {
    icon: BookOpen,
    title: "Education",
    description: "Knowledge and understanding are the foundations of peace."
  },
  {
    icon: Users,
    title: "Inclusivity",
    description: "We welcome all people regardless of background, working together for peace."
  }
];

const cornerstones = [
  {
    title: "Cultivating Personal Peace",
    description: "Helping individuals develop inner peace through mindfulness and self-awareness."
  },
  {
    title: "Empowering Community Peacebuilding",
    description: "Supporting communities in developing their own peace initiatives."
  },
  {
    title: "Teaching Peace in Schools",
    description: "Bringing peace education to young minds through school programs."
  },
  {
    title: "Humanizing Justice Systems",
    description: "Advocating for restorative justice practices that heal communities."
  },
  {
    title: "Fostering International Peace",
    description: "Building bridges across borders for global understanding."
  }
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="gradient-hero py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block bg-[#0056D2]/10 text-[#0056D2] px-4 py-2 rounded-full text-sm font-semibold mb-6">
                About Us
              </span>
              <h1 className="text-4xl sm:text-5xl font-bold font-['Outfit'] text-[#0F172A] leading-tight mb-6">
                Peace for Change Alliance Inc.
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                Founded with a vision to create lasting peace in our communities, Peace for Change Alliance Inc. (PFCA) has been at the forefront of violence prevention and peace advocacy.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                We work tirelessly to educate, advocate, and mobilize people into action to transform systems and public policy toward a culture of peace.
              </p>
            </div>
            <div>
              <img
                src={HERO_IMAGE}
                alt="Peace advocacy event"
                className="rounded-3xl shadow-2xl w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Executive Director Section */}
      <section className="py-20 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <span className="inline-block bg-[#0056D2]/20 text-[#60A5FA] px-4 py-2 rounded-full text-sm font-semibold mb-6">
                Executive Director
              </span>
              <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-white mb-6">
                Tracey Dent
              </h2>
              <p className="text-lg text-slate-300 leading-relaxed mb-6">
                Tracey Dent is the founder and Executive Director of Peace for Change Alliance Inc., a dedicated community activist who has devoted his life to building safer, more peaceful communities in Milwaukee and beyond.
              </p>
              <p className="text-slate-400 leading-relaxed mb-6">
                With over a decade of experience in community organizing and violence prevention, Tracey has been instrumental in establishing Milwaukee as the only city in the United States to officially recognize November as Veterans Mental Health and Suicide Awareness Month. His innovative "22 Push-ups Challenge" has raised awareness about the 22 veterans lost to suicide each day.
              </p>
              <p className="text-slate-400 leading-relaxed mb-6">
                Through initiatives like Youth Victory Over Violence Week and the Sleepover for Education program, Tracey works directly with young people to provide alternatives to violence and support their educational success. His hands-on approach to community activism has touched thousands of lives throughout Milwaukee.
              </p>
              <p className="text-slate-400 leading-relaxed mb-8">
                Tracey's vision is simple yet powerful: to educate, advocate, and mobilize communities toward a culture of peace. Under his leadership, Peace for Change Alliance continues to grow its impact, partnering with schools, businesses, and community organizations to create lasting change.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button className="bg-[#0056D2] hover:bg-[#0046B2] text-white rounded-full px-6">
                    Contact Tracey
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/volunteer">
                  <Button variant="outline" className="border-slate-600 text-white hover:bg-slate-800 rounded-full px-6">
                    Join the Movement
                  </Button>
                </Link>
              </div>
            </div>
            <div className="order-1 lg:order-2 flex justify-center">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-[#0056D2]/30 to-[#60A5FA]/30 rounded-3xl blur-xl" />
                <img
                  src={TRACEY_DENT_IMAGE}
                  alt="Tracey Dent - Executive Director and Community Activist"
                  className="relative rounded-3xl shadow-2xl w-full max-w-md h-[500px] object-cover object-top border-4 border-white/10"
                />
                <div className="absolute -bottom-4 -right-4 bg-[#0056D2] text-white p-4 rounded-2xl shadow-lg">
                  <Award className="w-6 h-6 mb-1" />
                  <p className="text-sm font-semibold">Community</p>
                  <p className="text-xs text-blue-200">Activist</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Mission */}
            <div className="bg-[#F0F9FF] p-8 rounded-3xl">
              <div className="w-14 h-14 bg-[#0056D2] rounded-xl flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-white" />
              </div>
              <h2 className="text-2xl font-bold font-['Outfit'] text-[#0F172A] mb-4">Our Mission</h2>
              <p className="text-slate-600 leading-relaxed text-lg">
                To educate, advocate, and mobilize people into action to transform systems and public policy toward a culture of peace.
              </p>
            </div>

            {/* Vision */}
            <div className="bg-[#FEF3C7] p-8 rounded-3xl">
              <div className="w-14 h-14 bg-[#F59E0B] rounded-xl flex items-center justify-center mb-6">
                <Eye className="w-7 h-7 text-white" />
              </div>
              <h2 className="text-2xl font-bold font-['Outfit'] text-[#0F172A] mb-4">Our Vision</h2>
              <p className="text-slate-600 leading-relaxed text-lg">
                A world where communities thrive in peace, where conflicts are resolved through dialogue, and where every person has the opportunity to live free from violence.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Five Cornerstones */}
      <section className="py-20 bg-[#F0F9FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#0056D2] font-semibold text-sm uppercase tracking-wider">Our Approach</span>
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-[#0F172A] mt-4 mb-6">
              Five Cornerstones of Peace
            </h2>
            <p className="text-lg text-slate-600">
              Our work is guided by five essential pillars that form the foundation of lasting peace.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cornerstones.map((stone, index) => (
              <div 
                key={index} 
                className={`bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-300 ${
                  index === 4 ? "md:col-span-2 lg:col-span-1" : ""
                }`}
              >
                <div className="w-10 h-10 bg-[#0056D2] rounded-lg flex items-center justify-center mb-4 text-white font-bold">
                  {index + 1}
                </div>
                <h3 className="text-lg font-semibold font-['Outfit'] text-[#0F172A] mb-2">{stone.title}</h3>
                <p className="text-slate-600">{stone.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#0056D2] font-semibold text-sm uppercase tracking-wider">What We Stand For</span>
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-[#0F172A] mt-4 mb-6">
              Our Core Values
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center group">
                <div className="w-16 h-16 bg-[#E0F2FE] rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:bg-[#0056D2] transition-colors duration-300">
                  <value.icon className="w-8 h-8 text-[#0056D2] group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="text-lg font-semibold font-['Outfit'] text-[#0F172A] mb-2">{value.title}</h3>
                <p className="text-slate-600 text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Voices Against Violence */}
      <section className="py-20 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <Award className="w-16 h-16 text-[#F59E0B] mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-white mb-6">
              Voices Against Violence Coalition
            </h2>
            <p className="text-lg text-slate-300 leading-relaxed mb-8">
              Our Voices Against Violence Coalition brings together community leaders, advocates, and everyday people committed to ending violence in our neighborhoods. Through public awareness campaigns, educational programs, and community outreach, we're making a difference one voice at a time.
            </p>
            <Link to="/videos">
              <Button className="bg-white text-[#0F172A] hover:bg-slate-100 rounded-full px-8 py-3 font-semibold">
                Watch Our Videos
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#0056D2] to-[#0044A5] rounded-3xl p-12 text-center">
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-white mb-6">
              Join Us in Building Peace
            </h2>
            <p className="text-lg text-blue-100 max-w-2xl mx-auto mb-8">
              Whether through volunteering, donating, or spreading awareness, you can be part of the change. Together, we can build a more peaceful world.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/volunteer">
                <Button className="bg-white text-[#0056D2] hover:bg-blue-50 rounded-full px-8 py-3 font-semibold">
                  <Users className="w-5 h-5 mr-2" />
                  Volunteer
                </Button>
              </Link>
              <Link to="/donate">
                <Button className="bg-[#F59E0B] text-white hover:bg-[#D97706] rounded-full px-8 py-3 font-semibold">
                  <Heart className="w-5 h-5 mr-2" />
                  Donate
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
